import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  Briefcase,
  Mail,
  LogIn,
  Menu,
  X
} from 'lucide-react';
import Button from '../common/Button';
import logo from '../../static/images/logo.png';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Who We Are', path: '/about', icon: Users },
    { name: 'What We Do', path: '/services', icon: Briefcase },
    { name: 'Contact Us', path: '/contact-us', icon: Mail }
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`
        fixed top-0 w-full z-50 transition-all duration-300 border-b
        ${scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-card border-gray-200'
          : 'bg-white/90 backdrop-blur-sm border-gray-100'}
      `}
    >
      <div className={`container mx-auto px-4 lg:px-6 ${scrolled ? 'py-2' : 'py-3'}`}>
        <div className="flex items-center justify-between gap-4">

          {/* Logo */}
          <div
            className="cursor-pointer flex items-center shrink-0"
            onClick={() => handleNavigate('/')}
          >
            <img
              src={logo}
              alt="Metro Logistics"
              className={`${scrolled ? 'h-10' : 'h-12'} transition-all duration-300`}
            />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.path);
              return (
                <button
                  key={link.path}
                  onClick={() => handleNavigate(link.path)}
                  className={`
                    px-4 py-2.5 rounded-btn flex items-center gap-2 text-sm font-medium
                    transition-all duration-200
                    ${active
                      ? 'bg-brand text-white shadow-sm'
                      : 'text-secondary hover:bg-brand-light hover:text-brand'}
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {link.name}
                </button>
              );
            })}
          </div>

          {/* Desktop Login */}
          <div className="hidden lg:block shrink-0">
            <Button
              variant="primary"
              size="md"
              icon={LogIn}
              onClick={() => handleNavigate('/login')}
            >
              Agent Login
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-btn border border-gray-200 bg-white text-secondary hover:bg-gray-50 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-gray-200 shadow-medium animate-[slideDown_0.2s_ease-out]">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.path);
              return (
                <button
                  key={link.path}
                  onClick={() => handleNavigate(link.path)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-btn text-sm font-medium text-left
                    transition-all duration-200
                    ${active
                      ? 'bg-brand text-white'
                      : 'text-secondary hover:bg-brand-light hover:text-brand'}
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {link.name}
                </button>
              );
            })}

            <div className="pt-3 mt-2 border-t border-gray-100">
              <Button
                variant="primary"
                size="md"
                icon={LogIn}
                className="w-full"
                onClick={() => handleNavigate('/login')}
              >
                Agent Login
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
